import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Camera, Image as ImageIcon, X, ArrowLeft } from "lucide-react";

const wrap = "ti-dark -m-3 min-h-[calc(100vh-3rem)] w-full max-w-full overflow-x-hidden px-4 py-4 pb-28 sm:-m-5 sm:px-6 lg:-m-5 lg:-mb-5 lg:px-8 lg:py-5 lg:pb-6";
const FLD = "mt-1 h-11 w-full min-w-0 border-white/12 bg-white/5 text-base text-white placeholder:text-slate-500 sm:text-sm";

export default function SecurityMovement({ type }) {
  const nav = useNavigate();
  const isIn = type === "IN";
  const [f, setF] = useState({ license_plate: "", vehicle: "", condition: "", note: "", driver: "", receiver: "", phone: "", odometer: "", purpose: "", wo_number: "", branch: "Pusat", exception_reason: "" });
  const [photos, setPhotos] = useState([]);
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);
  const [linked, setLinked] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const addPhotos = (list, src) => {
    Array.from(list).slice(0, 6 - photos.length).forEach((file) => {
      if (file.size > 10 * 1048576) { toast.error(`${file.name}: maksimal 10MB`); return; }
      setPhotos((p) => [...p, { file, src, preview: URL.createObjectURL(file), tempId: Math.random().toString(36).slice(2) }]);
    });
  };
  const removePhoto = (t) => setPhotos((p) => p.filter((x) => { if (x.tempId === t && x.preview) URL.revokeObjectURL(x.preview); return x.tempId !== t; }));

  const submit = async () => {
    if (!f.license_plate.trim()) { toast.error("Nomor polisi wajib"); return; }
    if (photos.length === 0) { toast.error("Minimal 1 foto wajib"); return; }
    setBusy(true);
    try {
      const idem = `${type}-${f.license_plate}-${Date.now()}`;
      const { data } = await api.post("/security/movements", { ...f, type, idempotency_key: idem });
      for (const p of photos) {
        const fd = new FormData(); fd.append("file", p.file); fd.append("source", p.src);
        await api.post(`/security/movements/${data.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      try { await api.post("/security/reconcile", {}); } catch (e) {}
      setSaved(data); setLinked(data.linked_in);
      toast.success(`Tersimpan: ${data.number}${data.unregistered ? " (UNIT BELUM TERDAFTAR)" : ""}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  if (saved) return (
    <div className={wrap} data-testid="security-saved">
      <div className="mx-auto w-full min-w-0 max-w-md text-center">
        <div className="ti-dcard p-6">
          <div className="text-emerald-300 text-lg font-bold">✓ {isIn ? "Unit Masuk" : "Unit Keluar"} tersimpan</div>
          <div className="mt-2 font-mono text-sm text-blue-200">{saved.number}</div>
          {saved.exception && <div className="mt-2 rounded bg-rose-500/15 px-2 py-1 text-xs text-rose-200">Pengecualian: {saved.exception}</div>}
          {linked && <div className="mt-2 text-xs text-[#AAB8CC]">Masuk: {linked.server_time_wib} · lama {linked.stay_hours ?? "-"} jam</div>}
          <Button data-testid="back-pos-btn" onClick={() => nav("/security/pos")} className="mt-4 h-11 w-full bg-blue-600 hover:bg-blue-500">Kembali ke Pos</Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className={wrap} data-testid={`security-form-${type}`}>
      <div className="mx-auto w-full min-w-0 max-w-md space-y-3">
        <button onClick={() => nav("/security/pos")} className="flex items-center gap-1.5 text-sm text-[#AAB8CC]"><ArrowLeft size={16} /> Pos Security</button>
        <h1 className={`font-heading text-xl font-extrabold ${isIn ? "text-emerald-300" : "text-blue-300"}`}>{isIn ? "UNIT MASUK" : "UNIT KELUAR"}</h1>
        <div className="ti-dcard space-y-3 p-4">
          <div><Label className="text-slate-300">Nomor Polisi *</Label><Input data-testid="in-plate" value={f.license_plate} onChange={set("license_plate")} placeholder="B 1234 XYZ" className={FLD} /></div>
          <div><Label className="text-slate-300">Nomor WO (opsional)</Label><Input data-testid="in-wo" value={f.wo_number} onChange={set("wo_number")} className={FLD} /></div>
          <div><Label className="text-slate-300">Kendaraan / Merek</Label><Input value={f.vehicle} onChange={set("vehicle")} className={FLD} /></div>
          <div><Label className="text-slate-300">{isIn ? "Pengemudi/Pengantar" : "Penerima/Pengemudi"}</Label><Input data-testid="in-driver" value={isIn ? f.driver : f.receiver} onChange={set(isIn ? "driver" : "receiver")} className={FLD} /></div>
          {isIn && <div><Label className="text-slate-300">Tujuan Kedatangan</Label><Input value={f.purpose} onChange={set("purpose")} placeholder="Klaim baru / Booking / Rework…" className={FLD} /></div>}
          <div><Label className="text-slate-300">Kondisi / Keterangan</Label><Input data-testid="in-note" value={isIn ? f.condition : f.note} onChange={set(isIn ? "condition" : "note")} className={FLD} /></div>
          {!isIn && <div><Label className="text-slate-300">Alasan (jika tanpa catatan masuk)</Label><Input data-testid="in-exception" value={f.exception_reason} onChange={set("exception_reason")} className={FLD} /></div>}
        </div>

        <div className="ti-dcard p-4">
          <Label className="text-slate-300">Foto kendaraan * (min 1)</Label>
          <div className="mt-2 flex gap-2">
            <label className="flex h-11 flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-lg border border-[#29466D] bg-[#102544] text-[#C3D0E2]" data-testid="cam-btn"><Camera size={17} />Kamera<input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { addPhotos(e.target.files, "camera"); e.target.value = ""; }} /></label>
            <label className="flex h-11 flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-lg border border-[#29466D] bg-[#102544] text-[#C3D0E2]" data-testid="gallery-btn"><ImageIcon size={17} />Galeri<input type="file" accept="image/jpeg,image/png,image/heic" multiple className="hidden" onChange={(e) => { addPhotos(e.target.files, "gallery"); e.target.value = ""; }} /></label>
          </div>
          {photos.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {photos.map((p) => (
                <div key={p.tempId} className="relative h-20 w-20 overflow-hidden rounded-lg border border-white/10">
                  <img src={p.preview} alt="" className="h-full w-full object-cover" />
                  <button onClick={() => removePhoto(p.tempId)} className="absolute right-0 top-0 flex h-5 w-5 items-center justify-center rounded-bl bg-rose-500/90 text-white"><X size={11} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        <Button data-testid="save-movement-btn" onClick={submit} disabled={busy} className={`h-12 w-full text-base font-bold ${isIn ? "bg-emerald-600 hover:bg-emerald-500" : "bg-blue-600 hover:bg-blue-500"}`}>{busy ? "Menyimpan…" : `Simpan Laporan ${isIn ? "Masuk" : "Keluar"}`}</Button>
      </div>
    </div>
  );
}
